import { Alert, Box, Chip, Paper, Stack, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useTranslation } from "react-i18next";
import { useAppCustomStyles } from "../../../../assets/theme/custom/hooks";
import { AppAutocomplete, AppCheckbox, AppRadioGroup, AppTextField } from "../../../../components/form";
import { AppFormField, AppFormGrid, PageSectionCard } from "../../../../components/layout";
import { GRID_HALF } from "../../../../lib/ui/formLayout";
import type { PrepGraphEdge, PrepGraphNode } from "../../../../types/prep";
import { edgeFieldMappingCompatible } from "../../utils/prepFieldMapping";

type Props = {
  draftEdge: PrepGraphEdge;
  sourceNode?: PrepGraphNode;
  targetNode?: PrepGraphNode;
  edgeSummary?: string;
  upstreamFields: string[];
  downstreamFields: string[];
  upstreamTested: boolean;
  downstreamTested: boolean;
  upstreamSampleRows?: Record<string, unknown>[];
  downstreamSampleRows?: Record<string, unknown>[];
  onChange: (edge: PrepGraphEdge) => void;
};

function nodeDisplayLabel(node?: PrepGraphNode): string {
  if (!node) return "?";
  const label = node.label?.trim();
  if (label) return label;
  return node.type === "OUTPUT"
    ? "Output"
    : node.type === "ADAPTOR_QUERY"
      ? "Adaptor"
      : node.type === "OPENAPI_QUERY"
        ? "OpenAPI"
        : "DSL Query";
}

export default function PrepEdgeConfigForm({
  draftEdge,
  sourceNode,
  targetNode,
  edgeSummary,
  upstreamFields,
  downstreamFields,
  upstreamTested,
  downstreamTested,
  upstreamSampleRows,
  downstreamSampleRows,
  onChange,
}: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const isOutputEdge = targetNode?.type === "OUTPUT";

  const sourceLabel = nodeDisplayLabel(sourceNode);
  const targetLabel = nodeDisplayLabel(targetNode);
  const joinType = draftEdge.joinType ?? "INNER";

  const upstreamHelper =
    upstreamFields.length === 0
      ? !upstreamTested
        ? t("prep.drawer.upstreamNotTested")
        : t("prep.drawer.noUpstreamFields")
      : undefined;

  const downstreamHelper =
    downstreamFields.length === 0
      ? !downstreamTested
        ? t("prep.drawer.downstreamNotTested")
        : t("prep.drawer.noDownstreamFields")
      : undefined;

  const fieldMappingCompatible = edgeFieldMappingCompatible(
    upstreamSampleRows,
    draftEdge.upstreamField,
    downstreamSampleRows,
    draftEdge.downstreamField
  );
  const showFieldMappingError =
    !isOutputEdge &&
    Boolean(draftEdge.upstreamField?.trim()) &&
    Boolean(draftEdge.downstreamField?.trim()) &&
    !fieldMappingCompatible;

  return (
    <Stack spacing={2.5}>
      <Paper
        variant="outlined"
        sx={custom.components.dataPrep.edgeConnectionPaper}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={custom.components.dataPrep.edgeConnectionLabel}>
          {t("prep.drawer.edgeConnection")}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={sourceLabel} size="small" variant="outlined" color="primary" />
          <ArrowForwardIcon fontSize="small" sx={custom.components.dataPrep.edgeArrowIcon} aria-hidden />
          <Chip label={targetLabel} size="small" variant="outlined" color="primary" />
          {!isOutputEdge && (
            <Chip
              label={joinType}
              size="small"
              sx={custom.components.dataPrep.edgeJoinChip}
            />
          )}
        </Stack>
        {edgeSummary && (
          <Typography variant="body2" color="text.secondary" sx={custom.components.dataPrep.edgeSummaryText}>
            {edgeSummary}
          </Typography>
        )}
      </Paper>

      {isOutputEdge ? (
        <>
          <Alert severity="info">{t("prep.drawer.edgeOutputPassthrough")}</Alert>
          {!upstreamTested && (
            <Alert severity="warning">{t("prep.drawer.edgeOutputSourceNotTested")}</Alert>
          )}
        </>
      ) : (
        <>
          {!upstreamTested && <Alert severity="warning">{t("prep.drawer.upstreamNotTested")}</Alert>}
          {!downstreamTested && <Alert severity="info">{t("prep.drawer.downstreamNotTested")}</Alert>}
          {showFieldMappingError && (
            <Alert severity="error">{t("prep.drawer.incompatibleFieldMapping")}</Alert>
          )}

          <PageSectionCard title={t("prep.drawer.fieldMapping")} noPadding>
            <Box sx={custom.components.dataPrep.edgeFormPadding}>
              <AppFormGrid spacing="compact">
                <AppFormField size={GRID_HALF} align="stretch">
                  {upstreamFields.length > 0 ? (
                    <AppAutocomplete
                      key={`upstream-${upstreamFields.join("|")}`}
                      label={t("prep.drawer.upstreamField")}
                      options={upstreamFields}
                      value={draftEdge.upstreamField?.trim() ? draftEdge.upstreamField : null}
                      onChange={(_, value) => onChange({ ...draftEdge, upstreamField: value ?? "" })}
                      helperText={upstreamHelper}
                      slotProps={{ popper: { sx: custom.components.dataPrep.autocompletePopperAboveModal } }}
                    />
                  ) : (
                    <AppTextField
                      label={t("prep.drawer.upstreamField")}
                      value=""
                      disabled
                      placeholder={t("prep.drawer.selectField")}
                      helperText={upstreamHelper}
                      fullWidth
                    />
                  )}
                </AppFormField>
                <AppFormField size={GRID_HALF} align="stretch">
                  {downstreamFields.length > 0 ? (
                    <AppAutocomplete
                      key={`downstream-${downstreamFields.join("|")}`}
                      label={t("prep.drawer.downstreamField")}
                      options={downstreamFields}
                      value={draftEdge.downstreamField?.trim() ? draftEdge.downstreamField : null}
                      onChange={(_, value) => onChange({ ...draftEdge, downstreamField: value ?? "" })}
                      helperText={downstreamHelper}
                      slotProps={{ popper: { sx: custom.components.dataPrep.autocompletePopperAboveModal } }}
                    />
                  ) : (
                    <AppTextField
                      label={t("prep.drawer.downstreamField")}
                      value=""
                      disabled
                      placeholder={t("prep.drawer.selectField")}
                      helperText={downstreamHelper}
                      fullWidth
                    />
                  )}
                </AppFormField>
              </AppFormGrid>
            </Box>
          </PageSectionCard>

          <PageSectionCard title={t("prep.drawer.joinOptions")} noPadding>
            <Box sx={custom.components.dataPrep.edgeJoinOptionsPadding}>
              <AppRadioGroup
                label={t("prep.drawer.joinType")}
                value={joinType}
                onChange={(value) => onChange({ ...draftEdge, joinType: value as "INNER" | "LEFT" })}
                options={[
                  { value: "INNER", label: "INNER" },
                  { value: "LEFT", label: "LEFT" },
                ]}
                formControlProps={{ margin: "none" }}
              />
              <Box sx={custom.components.dataPrep.edgeCheckboxMargin}>
                <AppCheckbox
                  label={t("prep.drawer.filterByUpstream")}
                  checked={draftEdge.filterByUpstreamValues ?? false}
                  onChange={(e) => onChange({ ...draftEdge, filterByUpstreamValues: e.target.checked })}
                />
              </Box>
            </Box>
          </PageSectionCard>
        </>
      )}
    </Stack>
  );
}
